import {inject, injectable} from 'inversify';

import type {BNBoundFormApiModel} from '../../../../../api/types/bnrks/models';
import type {CoreBoundFormApiModel} from '../../../../../api/types/core/models';
import type {Combine} from '../../../../../utils/utility-types';
import type {IApiDataMapper} from '../../../../ports/types';
import type {QuestionModel} from '../../../Question/Question.model';

import {AnswerMapperJrpc} from '../../../Answer/mappers/jrpc/Answer.mapper.jrpc';
import {AssessmentMapperJrpc} from '../../../Assessment/mappers/jrpc/Assessment.mapper.jrpc';
import {DateTimeMapperJrpc} from '../../../Date/mappers/DateTime.mapper.jrpc';
import {UserMapperJrpc} from '../../../User/mappers/User.mapper.jrpc';
import {BoundAssessmentFactory} from '../../BoundAssessment.factory';
import {BoundAssessmentModel} from '../../BoundAssessment.model';
import {BoundAssessmentExpandJrpc} from './BoundAssessment.expand.jrpc';

@injectable()
export class BoundAssessmentMapperJrpc
  implements
    IApiDataMapper<
      BoundAssessmentMapperJrpc.ExpandedApiData,
      BoundAssessmentModel.Props,
      BoundAssessmentModel
    >
{
  constructor(
    @inject(AssessmentMapperJrpc)
    private readonly assessmentMapper: AssessmentMapperJrpc,
    @inject(BoundAssessmentFactory)
    private readonly factory: BoundAssessmentFactory,
    @inject(DateTimeMapperJrpc)
    private readonly dateTimeMapper: DateTimeMapperJrpc,
    @inject(UserMapperJrpc)
    private readonly userMapper: UserMapperJrpc,
    @inject(AnswerMapperJrpc)
    private readonly answerMapper: AnswerMapperJrpc,
    @inject(BoundAssessmentExpandJrpc)
    readonly expand: BoundAssessmentExpandJrpc,
  ) {}

  mapDataToModel(data: BoundAssessmentMapperJrpc.ExpandedApiData) {
    return this.factory.create(this.mapDataToProps(data));
  }

  mapDataToProps(
    data: BoundAssessmentMapperJrpc.ExpandedApiData,
  ): BoundAssessmentModel.Props {
    const assessment = this.assessmentMapper.mapToModelOrEmpty(data.form);

    return {
      answered: this.answered(data),
      answers: this.answers(data, assessment.questions),
      assessment,
      author: this.userMapper.mapDataToModelOrEmpty(data.author),
      channelId: this.channelId(data),
      created: this.dateTimeMapper.mapToModel(data.created),
      id: data._id,
      score: data.score,
      status: this.status(data),
      title: data.title,
      updated: this.dateTimeMapper.mapToModel(data.updated),
    };
  }

  private answered(data: BoundAssessmentMapperJrpc.ExpandedApiData) {
    return data.answered
      ? this.dateTimeMapper.mapToModel(data.answered)
      : undefined;
  }

  private answers(
    data: BoundAssessmentMapperJrpc.ExpandedApiData,
    questions: QuestionModel[],
  ) {
    return this.answerMapper.mapAnswersToModelsByQuestionId(
      data.answers,
      questions,
    );
  }

  private assessment(data: BoundAssessmentMapperJrpc.ExpandedApiData) {
    return this.assessmentMapper.mapToModelOrEmpty(data.form);
  }

  private channelId(data: BoundAssessmentMapperJrpc.ExpandedApiData) {
    return data.resource?.channel_ids[0];
  }

  private status(data: BoundAssessmentMapperJrpc.ExpandedApiData) {
    if (data.answered) {
      return BoundAssessmentModel.Status.COMPLETED;
    }

    if (Object.keys(data.answers).length) {
      return BoundAssessmentModel.Status.IN_PROGRESS;
    }

    return BoundAssessmentModel.Status.SENT;
  }
}

export namespace BoundAssessmentMapperJrpc {
  export type ApiData = Combine<CoreBoundFormApiModel, BNBoundFormApiModel>;

  export type ExpandedApiData = ApiData &
    BoundAssessmentExpandJrpc.ExpandedFields;
}
